/**
 * Building blocks shared by docs/UX.md 11–18.
 *
 * DESIGN_SYSTEM.md: one primary action per page header, status always carries an
 * icon or text (never colour alone), touch targets ≥44px, every colour from a
 * `--gz-*` token (see `admin.css`).
 */
import { useId, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Button, Card, Icon, Input, Skeleton, Tag } from "@xinghai/ui";
import type { IconId, Tone } from "@xinghai/ui";
import { ApiError } from "../../lib/api";

// --------------------------------------------------------------------------- //
// page chrome
// --------------------------------------------------------------------------- //

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="xz-adm__head">
      <div className="xz-adm__headtext">
        <h1 className="gz-type-heading">{title}</h1>
        {description != null && <p className="gz-muted">{description}</p>}
      </div>
      {actions != null && <div className="xz-adm__headactions">{actions}</div>}
    </header>
  );
}

export function LoadingBlock({ rows = 3, label = "正在加载" }: { rows?: number; label?: string }) {
  return (
    // `aria-label` is prohibited on a role-less `div` (axe `aria-prohibited-attr`),
    // and a loading placeholder is exactly what `role="status"` is for.
    <div className="xz-adm__loading xz-stack" role="status" aria-busy="true" aria-label={label}>
      <Skeleton variant="rect" height={64} />
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} variant="rect" height={48} />
      ))}
    </div>
  );
}

/** Failure notice that keeps the input and offers an executable retry. */
export function ErrorNotice({
  error,
  onRetry,
  title = "没能加载这部分数据",
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}) {
  const message =
    error instanceof ApiError
      ? error.message
      : error instanceof Error
        ? error.message
        : "未知错误，请稍后再试。";
  return (
    <div className="gz-notice gz-notice--error xz-adm__notice" role="alert">
      <Icon id="ui-error" size={20} />
      <div className="xz-adm__noticebody">
        <strong>{title}</strong>
        <p className="gz-caption">{message}</p>
      </div>
      {onRetry && (
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<Icon id="ui-refresh" size={16} />}
          onClick={onRetry}
        >
          重试
        </Button>
      )}
    </div>
  );
}

/** Loading → error → content, so every page handles all three the same way. */
export function QueryBoundary({
  isPending,
  isError,
  error,
  onRetry,
  skeletonRows,
  children,
}: {
  isPending: boolean;
  isError: boolean;
  error?: unknown;
  onRetry?: () => void;
  skeletonRows?: number;
  children: ReactNode;
}) {
  if (isPending) return <LoadingBlock {...(skeletonRows != null ? { rows: skeletonRows } : {})} />;
  if (isError) return <ErrorNotice error={error} {...(onRetry ? { onRetry } : {})} />;
  return <>{children}</>;
}

// --------------------------------------------------------------------------- //
// status presentation (icon + text + value, never colour alone)
// --------------------------------------------------------------------------- //

export function StatusTag({
  icon,
  tone = "neutral",
  children,
}: {
  icon: IconId;
  tone?: Tone;
  children: ReactNode;
}) {
  return (
    <Tag tone={tone}>
      <Icon id={icon} size={16} /> {children}
    </Tag>
  );
}

export function StatRow({
  items,
  label,
}: {
  label: string;
  items: Array<{ key: string; icon: IconId; label: string; value: ReactNode }>;
}) {
  return (
    <ul className="xz-adm__stats" aria-label={label}>
      {items.map((s) => (
        <li key={s.key} className="xz-adm__stat">
          <Icon id={s.icon} size={20} />
          <span className="xz-adm__statvalue gz-type-score">{s.value}</span>
          <span className="xz-adm__statlabel gz-caption">{s.label}</span>
        </li>
      ))}
    </ul>
  );
}

export interface GateProps {
  label: string;
  /** Raw metric, `null` when there is no data yet. */
  value: number | null;
  gate: number;
  /** `">="` (κ / 一致率 / 黄金题) or `"<="` (仲裁占比). */
  compare: ">=" | "<=";
  /** Server-computed verdict; recomputed locally when absent. */
  pass?: boolean | null;
  /** Render as a percentage instead of a plain number. */
  percent?: boolean;
  icon: IconId;
}

export function formatPercent(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}

export function formatNumber(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return v.toFixed(digits);
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "—";
  const s = ms / 1000;
  return `${s.toFixed(1)} 秒`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * 质量门槛红绿灯 — icon + wording + the number, so the verdict survives
 * greyscale and colour-blind viewing (DESIGN_SYSTEM.md 视觉规则).
 */
export function GateLight({ label, value, gate, compare, pass, percent = true, icon }: GateProps) {
  const known = value !== null && value !== undefined && !Number.isNaN(value);
  const ok = pass ?? (known ? (compare === ">=" ? value >= gate : value <= gate) : null);
  const shown = percent ? formatPercent(value) : formatNumber(value);
  const target = `${compare === ">=" ? "≥" : "≤"} ${percent ? formatPercent(gate, 0) : formatNumber(gate)}`;
  const state = ok === null ? "unknown" : ok ? "pass" : "fail";
  const stateText = ok === null ? "暂无数据" : ok ? "达标" : "未达标";
  const stateIcon: IconId = ok === null ? "ui-help" : ok ? "ui-check-circle" : "ui-warning";

  return (
    <div className="xz-gate" data-state={state}>
      <div className="xz-gate__top">
        <Icon id={icon} size={20} />
        <span className="xz-gate__label">{label}</span>
      </div>
      <p className="xz-gate__value gz-type-score">{shown}</p>
      <p className="xz-gate__state">
        <Icon id={stateIcon} size={16} />
        <span>{stateText}</span>
        <span className="gz-caption">门槛 {target}</span>
      </p>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// form helpers
// --------------------------------------------------------------------------- //

export interface PickerOption {
  value: string;
  label: string;
  /** Second line, also searched. */
  hint?: string;
  disabled?: boolean;
  /** Optional grouping key for the 「按…全选」 shortcuts. */
  group?: string | null;
}

/** Accessible checkbox list with optional search and select-all shortcuts. */
export function CheckboxPicker({
  label,
  options,
  value,
  onChange,
  searchable = false,
  searchPlaceholder = "搜索",
  groupLabel,
  emptyText = "没有可选项。",
  max,
}: {
  label: string;
  options: readonly PickerOption[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** e.g. "班级" — renders 「按班级全选」 buttons from `option.group`. */
  groupLabel?: string;
  emptyText?: string;
  /** Cap on how many rows render before scrolling kicks in (CSS handles it). */
  max?: number;
}) {
  const [query, setQuery] = useState("");
  const listId = useId();
  const selected = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.value.toLowerCase().includes(q) ||
        o.label.toLowerCase().includes(q) ||
        (o.hint ?? "").toLowerCase().includes(q),
    );
  }, [options, query]);

  const groups = useMemo(() => {
    if (!groupLabel) return [];
    const set = new Set<string>();
    for (const o of options) if (o.group) set.add(o.group);
    return [...set].sort();
  }, [options, groupLabel]);

  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange([...next]);
  };

  const enabled = filtered.filter((o) => !o.disabled);
  const allShownSelected = enabled.length > 0 && enabled.every((o) => selected.has(o.value));

  return (
    <div className="xz-picker">
      <div className="xz-picker__bar">
        <span className="xz-picker__label">
          {label}
          <span className="gz-caption"> 已选 {value.length}</span>
        </span>
        <div className="xz-picker__tools">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              onChange(
                allShownSelected
                  ? value.filter((v) => !enabled.some((o) => o.value === v))
                  : [...new Set([...value, ...enabled.map((o) => o.value)])],
              )
            }
          >
            {allShownSelected ? "全不选" : "全选"}
          </Button>
          {groups.map((g) => (
            <Button
              key={g}
              variant="ghost"
              size="sm"
              onClick={() =>
                onChange([
                  ...new Set([
                    ...value,
                    ...options.filter((o) => o.group === g && !o.disabled).map((o) => o.value),
                  ]),
                ])
              }
            >
              {groupLabel}：{g}
            </Button>
          ))}
        </div>
      </div>

      {searchable && (
        <Input
          type="search"
          dense
          value={query}
          aria-label={`${label} — ${searchPlaceholder}`}
          placeholder={searchPlaceholder}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}

      {filtered.length === 0 ? (
        <p className="gz-muted xz-picker__empty">{emptyText}</p>
      ) : (
        <ul
          className="xz-picker__list"
          id={listId}
          role="group"
          aria-label={label}
          style={max ? { maxBlockSize: `${max * 2.75}rem` } : undefined}
        >
          {filtered.map((o) => (
            <li key={o.value}>
              <label className="xz-picker__row">
                <input
                  type="checkbox"
                  checked={selected.has(o.value)}
                  disabled={o.disabled}
                  onChange={() => toggle(o.value)}
                />
                <span className="xz-picker__text">
                  <span>{o.label}</span>
                  {o.hint && <span className="gz-caption">{o.hint}</span>}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// one-time links & codes (ACCOUNTS.md §2/§4 — copy, never re-read)
// --------------------------------------------------------------------------- //

/**
 * Clipboard write with a `document.execCommand` fallback: the invite / reset
 * link is shown once, so a failed copy has to be visible rather than silent.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    const clip = globalThis.navigator?.clipboard;
    if (clip?.writeText) {
      await clip.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Read-only value + a 复制 button; the text stays selectable as a fallback. */
export function CopyField({
  value,
  label,
  hint,
  onCopied,
}: {
  value: string;
  label: string;
  hint?: ReactNode;
  onCopied?: (ok: boolean) => void;
}) {
  const [state, setState] = useState<"idle" | "ok" | "fail">("idle");
  const id = useId();

  const copy = async () => {
    const ok = await copyText(value);
    setState(ok ? "ok" : "fail");
    onCopied?.(ok);
  };

  return (
    <div className="xz-copy">
      <label className="xz-copy__label" htmlFor={id}>
        {label}
      </label>
      <div className="xz-copy__row">
        <input
          id={id}
          className="xz-input xz-adm__mono"
          readOnly
          value={value}
          onFocus={(e) => e.target.select()}
        />
        <Button
          variant="secondary"
          leadingIcon={<Icon id={state === "ok" ? "ui-check" : "ui-save"} size={16} />}
          onClick={() => void copy()}
        >
          {state === "ok" ? "已复制" : "复制"}
        </Button>
      </div>
      {state === "fail" && (
        <p className="gz-error" role="alert">
          浏览器拒绝了复制，请手动选中上面的内容复制。
        </p>
      )}
      {hint != null && <p className="gz-caption">{hint}</p>}
    </div>
  );
}

/** Simple two-column definition list used by the detail sheets. */
export function KeyValue({
  items,
}: {
  items: Array<{ key: string; label: string; value: ReactNode }>;
}) {
  return (
    <dl className="xz-adm__kv">
      {items.map((i) => (
        <div key={i.key}>
          <dt className="gz-caption">{i.label}</dt>
          <dd>{i.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Section wrapper so every page's cards look the same. */
export function Section({
  title,
  subtitle,
  actions,
  className,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Extra class on the panel — e.g. `xz-section--shrink` for a wide child. */
  className?: string;
  children: ReactNode;
}) {
  return (
    <Card
      title={title}
      subtitle={subtitle}
      actions={actions}
      padding="md"
      {...(className ? { className } : {})}
    >
      {children}
    </Card>
  );
}
